import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Plus, Search, Edit, Trash2, ChevronLeft, X, Eye, FileText, Phone, Mail, MapPin, Car, Building } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const statusColors = { draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700' };

export default function CustomersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('list');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', company: '', notes: '', vehicles: [] });
  const [vehicleForm, setVehicleForm] = useState({ year: '', make: '', model: '', vin: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchCustomers();
  }, [user, authLoading, navigate]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await api.get('/admin/customers', { params });
      setCustomers(data.customers || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { const t = setTimeout(fetchCustomers, 300); return () => clearTimeout(t); }, [search]);

  const viewCustomer = async (c) => {
    try {
      const { data } = await api.get(`/admin/customers/${c.id}`);
      setSelectedCustomer(data);
      setView('detail');
    } catch {}
  };

  const openCreateForm = () => {
    setEditingCustomer(null);
    setForm({ name: '', email: '', phone: '', address: '', company: '', notes: '', vehicles: [] });
    setShowForm(true);
  };

  const openEditForm = (c) => {
    setEditingCustomer(c);
    setForm({ name: c.name, email: c.email || '', phone: c.phone || '', address: c.address || '', company: c.company || '', notes: c.notes || '', vehicles: c.vehicles || [] });
    setShowForm(true);
  };

  const addVehicle = () => {
    if (!vehicleForm.year || !vehicleForm.make || !vehicleForm.model) return;
    setForm(p => ({ ...p, vehicles: [...p.vehicles, { ...vehicleForm, year: parseInt(vehicleForm.year) }] }));
    setVehicleForm({ year: '', make: '', model: '', vin: '', notes: '' });
  };

  const removeVehicle = (idx) => setForm(p => ({ ...p, vehicles: p.vehicles.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editingCustomer) {
        await api.put(`/admin/customers/${editingCustomer.id}`, form);
      } else {
        await api.post('/admin/customers', form);
      }
      setShowForm(false);
      fetchCustomers();
      if (selectedCustomer && editingCustomer?.id === selectedCustomer.id) viewCustomer(editingCustomer);
    } catch {} finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer? This cannot be undone.')) return;
    try { await api.delete(`/admin/customers/${id}`); fetchCustomers(); if (view === 'detail') setView('list'); } catch {}
  };

  if (authLoading) return <div className="max-w-6xl mx-auto px-4 py-12 animate-pulse"><div className="h-8 bg-gray-200 rounded w-48" /></div>;
  if (!user || user.role !== 'admin') return null;

  // ===== CUSTOMER DETAIL =====
  if (view === 'detail' && selectedCustomer) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="customer-detail-page">
        <button onClick={() => setView('list')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#0F766E] mb-4"><ChevronLeft size={16} /> Back to Customers</button>

        {/* Customer card */}
        <div className="bg-white border rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3" style={{ fontFamily: "'Chivo', sans-serif" }}>
                {selectedCustomer.name}
                {selectedCustomer.company && <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{selectedCustomer.company}</span>}
              </h1>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                {selectedCustomer.phone && <span className="flex items-center gap-1.5"><Phone size={14} className="text-[#0F766E]" /> {selectedCustomer.phone}</span>}
                {selectedCustomer.email && <span className="flex items-center gap-1.5"><Mail size={14} className="text-[#0F766E]" /> {selectedCustomer.email}</span>}
                {selectedCustomer.address && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#0F766E]" /> {selectedCustomer.address}</span>}
              </div>
              {selectedCustomer.notes && <p className="text-sm text-gray-500 mt-2 italic">{selectedCustomer.notes}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEditForm(selectedCustomer)} className="border px-3 py-2 rounded text-sm font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1"><Edit size={14} /> Edit</button>
              <Link to="/admin/invoices" className="bg-[#F97316] text-white px-4 py-2 rounded text-sm font-bold hover:bg-[#EA580C] flex items-center gap-1"><FileText size={14} /> New Invoice</Link>
            </div>
          </div>

          {/* Vehicles */}
          {selectedCustomer.vehicles?.length > 0 && (
            <div className="mt-5 pt-5 border-t">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Vehicles on File</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {selectedCustomer.vehicles.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 border rounded px-3 py-2">
                    <Car size={16} className="text-[#0F766E] shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{v.year} {v.make} {v.model}</p>
                      {v.vin && <p className="text-[10px] text-gray-500 font-mono">VIN: {v.vin}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Invoice History */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="font-bold text-gray-900 flex items-center gap-2"><FileText size={16} className="text-[#0F766E]" /> Invoice History ({selectedCustomer.invoices?.length || 0})</h3>
          </div>
          {(!selectedCustomer.invoices || selectedCustomer.invoices.length === 0) ? (
            <div className="text-center py-12 text-gray-400"><FileText size={36} className="mx-auto mb-2 opacity-50" /><p className="text-sm">No invoices yet for this customer</p></div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] font-bold uppercase tracking-wider text-gray-500 border-b">
                <th className="px-5 py-3">Invoice #</th><th className="px-5 py-3">Vehicle</th><th className="px-5 py-3">Items</th><th className="px-5 py-3">Total</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Date</th><th className="px-5 py-3 w-16"></th>
              </tr></thead>
              <tbody className="divide-y">
                {selectedCustomer.invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono font-bold text-[#0F766E]">{inv.invoice_number}</td>
                    <td className="px-5 py-3 text-gray-600">{inv.vehicle_info || '-'}</td>
                    <td className="px-5 py-3 text-gray-600">{inv.items?.length || 0} parts</td>
                    <td className="px-5 py-3 font-bold text-[#0F766E]">${inv.total?.toFixed(2)}</td>
                    <td className="px-5 py-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColors[inv.status] || ''}`}>{inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}</span></td>
                    <td className="px-5 py-3 text-gray-500 text-xs">{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3"><Link to="/admin/invoices" className="text-gray-400 hover:text-[#0F766E]"><Eye size={16} /></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ===== CUSTOMER LIST =====
  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="customers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link to="/admin" className="text-sm text-gray-500 hover:text-[#0F766E] flex items-center gap-1 mb-1"><ChevronLeft size={14} /> Admin Dashboard</Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Chivo', sans-serif" }}>Customers</h1>
          <p className="text-sm text-gray-500 mt-1">{customers.length} customer{customers.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openCreateForm} className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-6 rounded-lg flex items-center gap-2 transition-all active:scale-95 text-sm" data-testid="add-customer-btn">
          <Plus size={18} /> Add Customer
        </button>
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <Search size={18} className="absolute left-4 top-3.5 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, email, or company..." className="w-full border-2 border-gray-200 rounded-lg pl-11 pr-4 py-3 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="customer-search" />
      </div>

      {/* Customer List */}
      <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded" />)}</div>
        ) : customers.length === 0 ? (
          <div className="text-center py-16">
            <Users size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">{search ? 'No customers found' : 'No customers yet'}</h3>
            <p className="text-gray-500 text-sm mb-6">{search ? 'Try a different search term' : 'Add your first customer to get started'}</p>
            {!search && <button onClick={openCreateForm} className="bg-[#F97316] text-white font-bold py-2.5 px-6 rounded-lg inline-flex items-center gap-2"><Plus size={16} /> Add First Customer</button>}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="customers-table">
            <thead><tr className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">
              <th className="px-5 py-3">Customer</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Vehicles</th><th className="px-5 py-3">Invoices</th><th className="px-5 py-3">Total Billed</th><th className="px-5 py-3 w-28">Actions</th>
            </tr></thead>
            <tbody className="divide-y">
              {customers.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => viewCustomer(c)} data-testid={`customer-row-${c.id}`}>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-gray-900">{c.name}</p>
                    {c.company && <p className="text-xs text-gray-500 flex items-center gap-1"><Building size={10} /> {c.company}</p>}
                  </td>
                  <td className="px-5 py-4 text-gray-600 text-xs space-y-0.5">
                    {c.phone && <p className="flex items-center gap-1"><Phone size={10} /> {c.phone}</p>}
                    {c.email && <p className="flex items-center gap-1"><Mail size={10} /> {c.email}</p>}
                  </td>
                  <td className="px-5 py-4">
                    {c.vehicles?.length > 0 ? c.vehicles.map((v, i) => <span key={i} className="inline-block bg-gray-100 text-gray-700 text-[10px] font-medium px-2 py-0.5 rounded mr-1 mb-0.5">{v.year} {v.make} {v.model}</span>) : <span className="text-gray-400 text-xs">None</span>}
                  </td>
                  <td className="px-5 py-4 text-center font-semibold text-gray-700">{c.invoice_count || 0}</td>
                  <td className="px-5 py-4 font-bold text-[#0F766E]">{c.invoice_total > 0 ? `$${c.invoice_total.toFixed(2)}` : '-'}</td>
                  <td className="px-5 py-4" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => viewCustomer(c)} className="p-2 text-gray-400 hover:text-[#0F766E] rounded hover:bg-gray-100" title="View"><Eye size={15} /></button>
                      <button onClick={() => openEditForm(c)} className="p-2 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50" title="Edit"><Edit size={15} /></button>
                      <button onClick={() => handleDelete(c.id)} className="p-2 text-gray-400 hover:text-red-500 rounded hover:bg-red-50" title="Delete"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Customer Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-form-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-bold text-gray-900" style={{ fontFamily: "'Chivo', sans-serif" }}>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="customer-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Phone</label><input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="customer-form-phone" /></div>
                <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Email</label><input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="customer-form-email" /></div>
              </div>
              <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Company / Body Shop</label><input value={form.company} onChange={e => setForm(p => ({...p, company: e.target.value}))} placeholder="e.g. Rodriguez Collision Center" className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="customer-form-company" /></div>
              <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Address</label><input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="customer-form-address" /></div>
              <div><label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Notes</label><textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Special notes about this customer..." className="w-full border-2 border-gray-200 rounded px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none" data-testid="customer-form-notes" /></div>

              {/* Vehicles */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Vehicles ({form.vehicles.length})</label>
                {form.vehicles.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 border rounded px-3 py-2 mb-1.5 text-sm">
                    <Car size={14} className="text-[#0F766E] shrink-0" />
                    <span className="font-medium">{v.year} {v.make} {v.model}</span>
                    {v.vin && <span className="text-[10px] text-gray-500 font-mono">VIN: {v.vin}</span>}
                    <button onClick={() => removeVehicle(i)} className="ml-auto text-gray-400 hover:text-red-500"><X size={14} /></button>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <input placeholder="Year" value={vehicleForm.year} onChange={e => setVehicleForm(p => ({...p, year: e.target.value}))} className="border rounded px-2 py-1.5 text-xs w-16" data-testid="vehicle-form-year" />
                  <input placeholder="Make" value={vehicleForm.make} onChange={e => setVehicleForm(p => ({...p, make: e.target.value}))} className="border rounded px-2 py-1.5 text-xs w-20" data-testid="vehicle-form-make" />
                  <input placeholder="Model" value={vehicleForm.model} onChange={e => setVehicleForm(p => ({...p, model: e.target.value}))} className="border rounded px-2 py-1.5 text-xs flex-1" data-testid="vehicle-form-model" />
                  <input placeholder="VIN (opt)" value={vehicleForm.vin} onChange={e => setVehicleForm(p => ({...p, vin: e.target.value}))} className="border rounded px-2 py-1.5 text-xs w-28" />
                  <button onClick={addVehicle} className="bg-[#0F766E] text-white text-xs font-bold px-3 py-1.5 rounded hover:bg-[#0D5F59]" data-testid="add-vehicle-btn"><Plus size={12} /></button>
                </div>
              </div>

              <div className="flex gap-3 pt-3 border-t">
                <button onClick={() => setShowForm(false)} className="px-5 py-2.5 border rounded text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
                <button onClick={handleSave} disabled={saving || !form.name} className="flex-1 bg-[#0F766E] hover:bg-[#0D5F59] text-white font-bold py-2.5 rounded disabled:opacity-40" data-testid="save-customer-btn">{saving ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Add Customer'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
